import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ArrowUp, ArrowDown, Eye, EyeOff, RotateCcw, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableRow = ({ id, label, visible, onToggle, onUp, onDown, isFirst, isLast }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 rounded-md border bg-card hover:bg-muted/50 transition-colors"
      data-testid={`customizer-row-${id}`}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none p-0.5"
        aria-label="Glisser pour réordonner"
        data-testid={`customizer-drag-${id}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Checkbox
        checked={visible}
        onCheckedChange={() => onToggle(id)}
        data-testid={`customizer-toggle-${id}`}
      />
      {visible ? <Eye className="h-3.5 w-3.5 text-muted-foreground" /> : <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />}
      <Label className={`flex-1 text-sm cursor-pointer ${visible ? "" : "text-muted-foreground line-through"}`} onClick={() => onToggle(id)}>
        {label}
      </Label>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        disabled={isFirst}
        onClick={() => onUp(id)}
        data-testid={`customizer-up-${id}`}
        title="Monter"
      >
        <ArrowUp className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        disabled={isLast}
        onClick={() => onDown(id)}
        data-testid={`customizer-down-${id}`}
        title="Descendre"
      >
        <ArrowDown className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};

export const DashboardCustomizer = ({
  open,
  onOpenChange,
  sections,
  order,
  visibility,
  onSave,
  onReset,
}) => {
  const [localOrder, setLocalOrder] = useState(() => buildOrder(order, sections));
  const [localVis, setLocalVis] = useState(() => ({ ...visibility }));
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function buildOrder(savedOrder, allSections) {
    const allKeys = allSections.map((s) => s.key);
    const ordered = [];
    (savedOrder || []).forEach((k) => {
      if (allKeys.includes(k) && !ordered.includes(k)) ordered.push(k);
    });
    allKeys.forEach((k) => {
      if (!ordered.includes(k)) ordered.push(k);
    });
    return ordered;
  }

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setLocalOrder((prev) => {
      const oldIdx = prev.indexOf(active.id);
      const newIdx = prev.indexOf(over.id);
      if (oldIdx === -1 || newIdx === -1) return prev;
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const moveByKey = (key, dir) => {
    setLocalOrder((prev) => {
      const idx = prev.indexOf(key);
      const j = idx + dir;
      if (idx === -1 || j < 0 || j >= prev.length) return prev;
      return arrayMove(prev, idx, j);
    });
  };

  const toggle = (key) => {
    setLocalVis((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
  };

  const labelOf = (key) => sections.find((s) => s.key === key)?.label || key;

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(localOrder, localVis);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setSaving(true);
      await onReset();
      const defaultOrder = sections.map((s) => s.key);
      setLocalOrder(defaultOrder);
      setLocalVis({});
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dashboard-customizer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Personnaliser le tableau de bord</DialogTitle>
          <DialogDescription>
            Glisse les sections (icône <GripVertical className="h-3 w-3 inline" />) pour les réordonner, ou utilise les flèches. Décoche pour masquer. Tes préférences ne sont visibles que par toi.
          </DialogDescription>
        </DialogHeader>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={localOrder} strategy={verticalListSortingStrategy}>
            <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
              {localOrder.map((key, idx) => (
                <SortableRow
                  key={key}
                  id={key}
                  label={labelOf(key)}
                  visible={localVis[key] !== false}
                  onToggle={toggle}
                  onUp={(k) => moveByKey(k, -1)}
                  onDown={(k) => moveByKey(k, 1)}
                  isFirst={idx === 0}
                  isLast={idx === localOrder.length - 1}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={saving}
            data-testid="customizer-reset"
          >
            <RotateCcw className="h-4 w-4 mr-1" /> Réinitialiser
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving} data-testid="customizer-save">
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DashboardCustomizer;
